/**
 * №16.1 Stage C — the author inspector rail. Routes the current selection to its
 * editor: segment/decision → the unified "Opens when" trigger block + decision +
 * conditions + effects; outcome → code/label/ending; step → the 6-tab inspector.
 * Every field commits one patch through the one door.
 */
import { useState } from "react";
import type { SegmentView, TemplateDetail, WfPatch, ActionCatalog } from "../../hooks/use-workflow-templates";
import { TRIGGER_KINDS, triggerLabel, findingsFor } from "./wf-author-shared";
import { EffectsEditor, ConditionsEditor } from "./AuthorEditors";
import { StepInspector } from "./StepInspector";
import type { Selection } from "./TemplateCanvas";

type Patch = (patches: WfPatch[]) => void;
interface Ctx { detail: TemplateDetail; patch: Patch; busy: boolean; catalog: ActionCatalog | undefined; onSelect: (s: Selection | null) => void; onListBlocks: () => void }

function Findings({ detail, segmentNo, stepNo, onSelect }: { detail: TemplateDetail; segmentNo?: number; stepNo?: number; onSelect: (s: Selection | null) => void }) {
  const fs = findingsFor(detail, segmentNo, stepNo);
  if (fs.length === 0) return null;
  return (
    <div className="wfa-findings" style={{ marginTop: 12 }}>
      <div className="wfa-findings__hd">{fs.length} finding{fs.length > 1 ? "s" : ""} here</div>
      {fs.map((f, i) => (
        <button key={i} className="wfa-finding" onClick={() => f.segmentNo && onSelect({ kind: "segment", segmentNo: f.segmentNo, stepNo: f.stepNo })}>
          <span className={`sev ${f.severity}`}>{f.severity}</span><span>{f.message}</span>
        </button>
      ))}
    </div>
  );
}

// The unified "Opens when" trigger block — identical for a segment and its decision.
function TriggerEditor({ seg, detail, patch, busy }: { seg: SegmentView; detail: TemplateDetail; patch: Patch; busy: boolean }) {
  const others = detail.segments.filter((s) => s.segmentNo !== seg.segmentNo);
  const decisions = others.filter((s) => s.hasDecision);
  const setType = (kind: string) => {
    if (kind === "afterDecision") { const d = decisions[0]; patch([{ op: "updateSegment", segmentNo: seg.segmentNo, triggerKind: "afterDecision", triggerSegmentNo: d?.segmentNo ?? 0, triggerOutcomeCode: d?.outcomes[0]?.code ?? "" }]); }
    else if (kind === "afterSegments") patch([{ op: "updateSegment", segmentNo: seg.segmentNo, triggerKind: "afterSegments" }]);
    else if (kind === "date") patch([{ op: "updateSegment", segmentNo: seg.segmentNo, triggerKind: "date" }]);
    else patch([{ op: "updateSegment", segmentNo: seg.segmentNo, triggerKind: "withMatter" }]);
  };
  const toggleDep = (depNo: number) => {
    const on = seg.dependsOn.includes(depNo);
    patch([on ? { op: "removeSegmentDependency", segmentNo: seg.segmentNo, dependsOn: depNo } : { op: "addSegmentDependency", segmentNo: seg.segmentNo, dependsOn: depNo }]);
  };
  return (
    <div className="wfa-trigbox">
      <div className="th">◷ Opens when — same panel everywhere</div>
      <select value={seg.triggerKind} onChange={(e) => setType(e.target.value)} disabled={busy} style={{ width: "100%" }}>
        {TRIGGER_KINDS.filter((t) => !t.segmentOnly || true).map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        {(seg.triggerKind === "gateResolves" || seg.triggerKind === "stepsComplete") && <option value={seg.triggerKind}>{triggerLabel(seg, detail.segments)}</option>}
      </select>
      {seg.triggerKind === "afterSegments" && (
        <div className="wfa-members">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
            <span style={{ fontSize: 11, color: "var(--color-text-secondary)" }}>join</span>
            <select value={seg.joinMode} onChange={(e) => patch([{ op: "updateSegment", segmentNo: seg.segmentNo, joinMode: e.target.value }])} disabled={busy}>
              <option value="all">all (AND)</option><option value="any">any (OR)</option>
            </select>
          </div>
          {others.length === 0 && <div style={{ fontSize: 11, color: "var(--color-text-tertiary)" }}>no other segments yet</div>}
          {others.map((o) => (
            <div key={o.segmentNo} className={`wfa-mrow ${seg.dependsOn.includes(o.segmentNo) ? "on" : ""}`} onClick={() => !busy && toggleDep(o.segmentNo)}>
              <span className="cb">{seg.dependsOn.includes(o.segmentNo) ? "✓" : ""}</span>{o.name}
            </div>
          ))}
        </div>
      )}
      {seg.triggerKind === "afterDecision" && (
        <div className="wfa-members">
          <div className="wfa-fld"><label>Decision</label>
            <select value={seg.triggerSegmentNo} disabled={busy} onChange={(e) => { const d = decisions.find((x) => x.segmentNo === Number(e.target.value)); patch([{ op: "updateSegment", segmentNo: seg.segmentNo, triggerSegmentNo: Number(e.target.value), triggerOutcomeCode: d?.outcomes[0]?.code ?? "" }]); }}>
              {decisions.length === 0 && <option value={0}>— no decisions yet —</option>}
              {decisions.map((d) => <option key={d.segmentNo} value={d.segmentNo}>{d.decisionName || d.name}</option>)}
            </select>
          </div>
          <div className="wfa-fld"><label>Outcome</label>
            <select value={seg.triggerOutcomeCode ?? ""} disabled={busy} onChange={(e) => patch([{ op: "updateSegment", segmentNo: seg.segmentNo, triggerOutcomeCode: e.target.value }])}>
              {(decisions.find((d) => d.segmentNo === seg.triggerSegmentNo)?.outcomes ?? []).map((o) => <option key={o.code} value={o.code}>{o.label}</option>)}
            </select>
          </div>
        </div>
      )}
      {seg.triggerKind === "date" && (
        <div className="wfa-fld" style={{ marginTop: 8 }}><label>Date type (blank = standing lane)</label>
          <input value={seg.triggerDateType ?? ""} disabled={busy} onChange={(e) => patch([{ op: "updateSegment", segmentNo: seg.segmentNo, triggerDateType: e.target.value }])} placeholder="e.g. SETTLEMENT" />
        </div>
      )}
    </div>
  );
}

function SegmentInspector({ seg, ctx }: { seg: SegmentView; ctx: Ctx }) {
  const { detail, patch, busy, catalog, onSelect } = ctx;
  const subjectType = detail.template?.subjectType;
  const setName = (v: string) => patch([{ op: "updateSegment", segmentNo: seg.segmentNo, name: v }]);
  const toggleDecision = () => patch([{ op: "updateSegment", segmentNo: seg.segmentNo, hasDecision: !seg.hasDecision, ...(seg.hasDecision ? {} : { decisionName: seg.name }) }]);
  const addOutcome = () => patch([{ op: "addOutcome", segmentNo: seg.segmentNo, outcomeCode: `OPT${seg.outcomes.length + 1}`, label: "New outcome", isDefault: seg.outcomes.length === 0 }]);
  return (
    <div className="wfa-insp">
      <div className="kind">{seg.hasDecision ? "Decision segment" : "Segment"}</div>
      <div className="title">{seg.name}</div>
      <div className="wfa-fld"><label>Name</label><input defaultValue={seg.name} key={`n${seg.segmentNo}${seg.name}`} disabled={busy} onBlur={(e) => e.target.value !== seg.name && setName(e.target.value)} /></div>
      <TriggerEditor seg={seg} detail={detail} patch={patch} busy={busy} />

      {/* №16.1.1 — the Ordered⇄Unordered toggle (matrix §B, `stepsOrdered`). A
          segment is a parallel field (∥) OR a run-in-order list (▤) — a boolean,
          never a graph (r10). No step-level deps; ordered ⇒ step N is ready once
          every earlier step is terminal. */}
      <div className="wfa-fld" style={{ marginTop: 10 }}>
        <label>How its steps run</label>
        <div className="wfa-tabs" role="radiogroup" aria-label="step ordering" style={{ marginTop: 4 }}>
          <button role="radio" aria-checked={!seg.stepsOrdered} className={!seg.stepsOrdered ? "on" : ""} disabled={busy}
            onClick={() => seg.stepsOrdered && patch([{ op: "updateSegment", segmentNo: seg.segmentNo, stepsOrdered: false }])}>∥ Unordered</button>
          <button role="radio" aria-checked={seg.stepsOrdered} className={seg.stepsOrdered ? "on" : ""} disabled={busy}
            onClick={() => !seg.stepsOrdered && patch([{ op: "updateSegment", segmentNo: seg.segmentNo, stepsOrdered: true }])}>▤ In order</button>
        </div>
        <div className="wfa-note" style={{ marginTop: 6 }}>{seg.stepsOrdered ? "Steps run one after another — each opens when the previous finishes." : "All steps open together (parallel)."}</div>
      </div>

      <div className="wfa-fld" style={{ marginTop: 12 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <input type="checkbox" style={{ width: "auto" }} checked={seg.hasDecision} disabled={busy} onChange={toggleDecision} /> This segment asks a decision
        </label>
      </div>
      {seg.hasDecision && (
        <>
          <div className="wfa-fld"><label>Decision question</label><input defaultValue={seg.decisionName ?? ""} key={`d${seg.segmentNo}${seg.decisionName}`} disabled={busy} onBlur={(e) => patch([{ op: "updateSegment", segmentNo: seg.segmentNo, decisionName: e.target.value }])} /></div>
          <div className="wfa-k" style={{ marginTop: 4 }}>OUTCOMES</div>
          {seg.outcomes.map((o) => (
            <div key={o.code} className="wfa-cond" style={{ cursor: "pointer" }} onClick={() => onSelect({ kind: "outcome", segmentNo: seg.segmentNo, outcomeCode: o.code })}>
              <span className="subj">{o.label}</span><span className="op">{o.code}{o.isDefault ? " · default" : ""}</span>
              <i className="ti ti-chevron-right" style={{ marginLeft: "auto", color: "var(--color-text-tertiary)" }} aria-hidden="true" />
            </div>
          ))}
          <button className="wfa-add" disabled={busy} onClick={addOutcome}>+ Add outcome</button>
          <div style={{ marginTop: 14 }}><EffectsEditor effects={seg.effects} ownerKind="decision" ownerNo={seg.segmentNo} catalog={catalog} subjectType={subjectType} patch={patch} busy={busy} /></div>
        </>
      )}

      <div style={{ marginTop: 14 }}><ConditionsEditor conditions={seg.conditions} ownerKind="segment" ownerNo={seg.segmentNo} logic={seg.conditionLogic} catalog={catalog} subjectType={subjectType} patch={patch} busy={busy} /></div>
      <div style={{ marginTop: 14 }}><EffectsEditor effects={seg.effects} ownerKind="segment" ownerNo={seg.segmentNo} catalog={catalog} subjectType={subjectType} patch={patch} busy={busy} /></div>

      <Findings detail={detail} segmentNo={seg.segmentNo} onSelect={onSelect} />
      <button className="wfa-btn danger" style={{ marginTop: 16, width: "100%" }} disabled={busy} onClick={() => { patch([{ op: "deleteSegment", segmentNo: seg.segmentNo }]); onSelect(null); }}>Delete segment</button>
    </div>
  );
}

function OutcomeInspector({ seg, code, ctx }: { seg: SegmentView; code: string; ctx: Ctx }) {
  const { detail, patch, busy, catalog, onSelect } = ctx;
  const subjectType = detail.template?.subjectType;
  const o = seg.outcomes.find((x) => x.code === code);
  const ending = detail.endings.find((e) => e.segmentNo === seg.segmentNo && e.outcomeCode === code);
  if (!o) return <div className="wfa-empty">Outcome not found.</div>;
  return (
    <div className="wfa-insp">
      <div className="kind">Branch outcome</div>
      <div className="title">{o.label}</div>
      <div className="wfa-fld"><label>Code</label><input defaultValue={o.code} key={`oc${o.code}`} disabled={busy} onBlur={(e) => e.target.value !== o.code && patch([{ op: "addOutcome", segmentNo: seg.segmentNo, outcomeCode: e.target.value, label: o.label, isDefault: o.isDefault }])} /></div>
      <div className="wfa-fld"><label>Label</label><input defaultValue={o.label} key={`ol${o.code}${o.label}`} disabled={busy} onBlur={(e) => patch([{ op: "addOutcome", segmentNo: seg.segmentNo, outcomeCode: o.code, label: e.target.value, isDefault: o.isDefault }])} /></div>
      <div className="wfa-fld"><label style={{ display: "flex", gap: 8, alignItems: "center" }}><input type="checkbox" style={{ width: "auto" }} checked={o.isDefault} disabled={busy} onChange={() => patch([{ op: "addOutcome", segmentNo: seg.segmentNo, outcomeCode: o.code, label: o.label, isDefault: true }])} /> Default outcome</label></div>
      <div className="wfa-note">No “rejoin” by design — an outcome never names a successor. If work should follow, a <b>downstream</b> segment declares a trigger this outcome satisfies (pull, not push).</div>

      <div className="wfa-k" style={{ marginTop: 14 }}>ENDING (forward-chain)</div>
      {ending
        ? <div className="wfa-cond"><span className="subj">→ {ending.successorTemplateCode}</span><span className="op">{ending.label || "successor"}</span><button className="del" style={{ marginLeft: "auto" }} disabled={busy} onClick={() => patch([{ op: "removeEnding", segmentNo: seg.segmentNo, outcomeCode: o.code }])}>✕</button></div>
        : <div className="wfa-note" style={{ marginTop: 0 }}>This outcome continues in-workflow. To END and start another workflow, add an ending below.</div>}
      <AddEnding seg={seg} code={o.code} patch={patch} busy={busy} />

      <div style={{ marginTop: 14 }}><EffectsEditor effects={seg.effects} ownerKind="outcome" ownerNo={seg.segmentNo} outcomeCode={o.code} catalog={catalog} subjectType={subjectType} patch={patch} busy={busy} /></div>
      <button className="wfa-btn danger" style={{ marginTop: 16, width: "100%" }} disabled={busy} onClick={() => { patch([{ op: "removeOutcome", segmentNo: seg.segmentNo, outcomeCode: o.code }]); onSelect({ kind: "segment", segmentNo: seg.segmentNo }); }}>Remove outcome</button>
    </div>
  );
}

function AddEnding({ seg, code, patch, busy }: { seg: SegmentView; code: string; patch: Patch; busy: boolean }) {
  const [open, setOpen] = useState(false);
  const [successor, setSuccessor] = useState("");
  if (!open) return <button className="wfa-add" disabled={busy} onClick={() => setOpen(true)}>+ End here → start a workflow</button>;
  return (
    <div className="wfa-cond" style={{ flexWrap: "wrap", gap: 6 }}>
      <input placeholder="successor template code" value={successor} onChange={(e) => setSuccessor(e.target.value)} style={{ width: 180 }} />
      <button className="wfa-btn primary" disabled={busy || !successor} onClick={() => { patch([{ op: "addEnding", segmentNo: seg.segmentNo, outcomeCode: code, successorTemplateCode: successor, anchorMode: "same" }]); setOpen(false); setSuccessor(""); }}>Add</button>
      <button className="del" onClick={() => setOpen(false)}>cancel</button>
    </div>
  );
}

export function InspectorRail({ selected, ctx }: { selected: Selection | null; ctx: Ctx }) {
  if (!selected) return <div className="wfa-empty">Select a segment, decision, outcome, or step.<br /><br />The <b>Opens when</b> panel is the same for a segment and its decision.</div>;
  const seg = ctx.detail.segments.find((s) => s.segmentNo === selected.segmentNo);
  if (!seg) return <div className="wfa-empty">Not found.</div>;
  if (selected.kind === "step" && selected.stepNo != null) {
    const step = seg.steps.find((s) => s.stepNo === selected.stepNo);
    if (!step) return <div className="wfa-empty">Step not found.</div>;
    return <StepInspector seg={seg} step={step} detail={ctx.detail} patch={ctx.patch} busy={ctx.busy} catalog={ctx.catalog} onSelect={ctx.onSelect} />;
  }
  if (selected.kind === "outcome" && selected.outcomeCode) return <OutcomeInspector seg={seg} code={selected.outcomeCode} ctx={ctx} />;
  return <SegmentInspector seg={seg} ctx={ctx} />;
}

export type { Ctx as InspectorCtx };
